// RaciPicker: editable cousin of RaciRail. Used by NewTaskModal today;
// Lane B.1.c will mount it inline on TaskDetail behind an "Edit owners"
// affordance. Stateless: owns nothing internally, calls onChange with
// the full new owners[] every time.
//
// Layout matches RaciRail (4 vertical blocks R/A/C/I) so users see the
// same shape in edit mode as read mode — no mental remap.

import { useState } from 'react';
import { ROLE_LABELS, ROLE_DESCRIPTIONS } from '../lib/owners.ts';
import { Avatar } from './Avatar.jsx';
import { Icon } from './Icon.jsx';

const ROLE_ORDER = ['R', 'A', 'C', 'I'];

export const RaciPicker = ({ owners, staff, onChange }) => {
  // Per-block UI state: which role has its "+ Add" dropdown currently open.
  const [openRole, setOpenRole] = useState(null);

  const byRole = ROLE_ORDER.reduce((acc, r) => {
    acc[r] = owners.filter(o => o.role === r);
    return acc;
  }, {});

  // Staff already assigned to ANY role on this task — we don't show them
  // in the "+ Add" dropdowns because the same person should not hold two
  // RACI roles on the same task (rare in practice; if needed, drop this filter).
  const assignedIds = new Set(owners.map(o => o.staffId));
  const availableStaff = staff.filter(s => s.active && !assignedIds.has(s.id));

  function addOwner(role, staffId) {
    if (!staffId) return;
    // First R added auto-becomes primary so a brand-new task has one without
    // the user having to click the star. Same rule the worker uses.
    const isFirstR = role === 'R' && !owners.some(o => o.role === 'R');
    onChange([...owners, { staffId, role, isPrimary: isFirstR }]);
    setOpenRole(null);
  }

  function removeOwner(idx) {
    const removed = owners[idx];
    const next = owners.slice();
    next.splice(idx, 1);
    // If we removed the primary R, promote the next R (if any) so the
    // 主负责人 invariant holds locally before save.
    if (removed?.role === 'R' && removed?.isPrimary) {
      const nextR = next.find(o => o.role === 'R');
      if (nextR) nextR.isPrimary = true;
    }
    onChange(next);
  }

  function setPrimary(staffId) {
    onChange(owners.map(o =>
      o.role === 'R' ? { ...o, isPrimary: o.staffId === staffId } : o
    ));
  }

  return (
    <div className="raci-picker">
      {ROLE_ORDER.map(role => (
        <div className={`raci-block raci-block-${role}`} key={role}>
          <div className="raci-block-label" title={ROLE_DESCRIPTIONS[role]}>
            <span className={`raci-role-badge role-${role}`}>{role}</span>
            <span className="raci-role-name">{ROLE_LABELS[role]}</span>
            {byRole[role].length > 0 && (
              <span className="raci-role-count">· {byRole[role].length}</span>
            )}
            {role === 'R' && byRole[role].length === 0 && (
              <span className="raci-role-required" aria-label="必填">必填</span>
            )}
          </div>

          <div className="raci-block-owners">
            {byRole[role].map(o => {
              const s = staff.find(x => x.id === o.staffId);
              const idx = owners.indexOf(o);
              const isPrimary = role === 'R' && o.isPrimary;
              const canPromote = role === 'R' && !o.isPrimary && byRole.R.length > 1;
              return (
                <div className={`multi-owner-pill multi-owner-pill-editable${isPrimary ? ' is-primary' : ''}`}
                     key={`${o.staffId}-${o.role}`}>
                  {isPrimary && <span className="multi-owner-star" title="主负责人">★</span>}
                  <Avatar staff={s}/>
                  <span className="multi-owner-name">{s?.name || o.staffId}</span>
                  {canPromote && (
                    <button type="button" className="multi-owner-promote"
                            onClick={() => setPrimary(o.staffId)}
                            title="设为主负责人">设为主</button>
                  )}
                  <button type="button" className="multi-owner-remove"
                          onClick={() => removeOwner(idx)}
                          aria-label={`移除 ${s?.name || o.staffId}`}>
                    <Icon name="x" className="icon-sm"/>
                  </button>
                </div>
              );
            })}

            {openRole === role ? (
              <select className="vava-select raci-add-select"
                      value=""
                      autoFocus
                      onChange={(e) => addOwner(role, e.target.value)}
                      onBlur={() => setOpenRole(null)}>
                <option value="">选择员工...</option>
                {availableStaff.map(s => (
                  <option key={s.id} value={s.id}>{s.name} · {s.title}</option>
                ))}
              </select>
            ) : (
              <button type="button" className="raci-add-btn"
                      onClick={() => setOpenRole(role)}
                      disabled={availableStaff.length === 0}>
                <Icon name="plus" className="icon-sm"/> 添加 {ROLE_LABELS[role]}
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
